import { useCallback, useEffect, useRef, useState } from "react";
import { ClientEvent, type MatrixClient } from "matrix-js-sdk/src/matrix";

import { createReconnectedListener } from "../utils/connection";

/**
 * Manages map error state and automatically clears it when the Matrix client reconnects.
 * Mirrors the pattern used in MLocationBody (class-based) for functional components.
 */
export function useLocationMapError(matrixClient: MatrixClient | null | undefined): {
    mapError: Error | undefined;
    onMapError: (error: Error) => void;
} {
    const [mapError, setMapError] = useState<Error | undefined>();

    // Keep a stable ref so listener closures always see the current client
    const matrixClientRef = useRef(matrixClient);
    matrixClientRef.current = matrixClient;

    // Create the reconnect listener once; it clears the error and unregisters itself
    const reconnectedListenerRef = useRef(
        createReconnectedListener(() => {
            matrixClientRef.current?.off(ClientEvent.Sync, reconnectedListenerRef.current);
            setMapError(undefined);
        }),
    );

    const onMapError = useCallback((error: Error) => {
        setMapError(error);
        // Unregister first to avoid duplicate listeners
        matrixClientRef.current?.off(ClientEvent.Sync, reconnectedListenerRef.current);
        matrixClientRef.current?.on(ClientEvent.Sync, reconnectedListenerRef.current);
    }, []);

    // Clean up listener on unmount
    useEffect(() => {
        const listener = reconnectedListenerRef.current;
        const clientRef = matrixClientRef;
        return () => {
            clientRef.current?.off(ClientEvent.Sync, listener);
        };
    }, []);

    return { mapError, onMapError };
}
